import {
  Box,
  Text,
  Group,
  Avatar,
  Stack,
  ActionIcon,
  ScrollArea,
  Tabs,
  Indicator,
  useMantineTheme,
  Popover,
  Badge,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { forwardRef } from 'react';
import {
  IconBell,
  IconBellOff,
  IconCheck,
  IconChecks,
  IconTrash,
  IconSettings,
  IconX,
} from '@tabler/icons-react';
import { Paper, Button } from '@/components/ui';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface NotificationItem {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'message' | 'update' | 'alert';
  title: string;
  message: string;
  timestamp: Date | string;
  read?: boolean;
  avatar?: string;
  link?: string;
  actions?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'primary' | 'danger';
  }[];
}

export interface NotificationCenterProps {
  notifications: NotificationItem[];
  unreadCount?: number;
  onMarkAsRead?: (id: string) => void;
  onMarkAllAsRead?: () => void;
  onDelete?: (id: string) => void;
  onClearAll?: () => void;
  onNotificationClick?: (notification: NotificationItem) => void;
  onSettingsClick?: () => void;
  maxHeight?: number | string;
  showTabs?: boolean;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  position?: 'bottom' | 'bottom-end' | 'bottom-start';
  triggerElement?: React.ReactNode;
}

const formatRelativeTime = (date: Date | string): string => {
  const now = new Date();
  const then = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return then.toLocaleDateString();
};

const getTypeColor = (type: NotificationItem['type']): string => {
  switch (type) {
    case 'success':
      return 'teal';
    case 'warning':
      return 'yellow';
    case 'error':
      return 'red';
    case 'info':
      return 'blue';
    default:
      return 'gray';
  }
};

const NotificationCenter = forwardRef<HTMLDivElement, NotificationCenterProps>(
  (
    {
      notifications,
      unreadCount,
      onMarkAsRead,
      onMarkAllAsRead,
      onDelete,
      onClearAll,
      onNotificationClick,
      onSettingsClick,
      maxHeight = 400,
      showTabs = true,
      gradientVariant = 'primary',
      position = 'bottom-end',
      triggerElement,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const [opened, { toggle, close }] = useDisclosure(false);

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const unread = unreadCount ?? notifications.filter((n) => !n.read).length;
    const unreadNotifications = notifications.filter((n) => !n.read);
    const allNotifications = notifications;

    const renderNotification = (notification: NotificationItem) => (
      <Box
        key={notification.id}
        p="sm"
        style={{
          backgroundColor: notification.read ? undefined : `${gradient?.from}08`,
          borderLeft: notification.read ? '3px solid transparent' : `3px solid ${gradient?.from}`,
          cursor: 'pointer',
          transition: 'background-color 0.2s',
        }}
        onClick={() => {
          onNotificationClick?.(notification);
          if (!notification.read) {
            onMarkAsRead?.(notification.id);
          }
        }}
      >
        <Group wrap="nowrap" align="flex-start">
          {notification.avatar ? (
            <Avatar src={notification.avatar} size="sm" radius="xl" />
          ) : (
            <Box
              w={32}
              h={32}
              style={{
                borderRadius: '50%',
                backgroundColor: `${theme.colors[getTypeColor(notification.type)][1]}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconBell size={16} color={theme.colors[getTypeColor(notification.type)][6]} />
            </Box>
          )}

          <Box style={{ flex: 1, minWidth: 0 }}>
            <Group justify="space-between" gap={4}>
              <Text size="sm" fw={600} truncate style={accessibilityStyles}>
                {notification.title}
              </Text>
              <Group gap={4}>
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  {formatRelativeTime(notification.timestamp)}
                </Text>
                {!notification.read && (
                  <Box
                    w={8}
                    h={8}
                    style={{
                      borderRadius: '50%',
                      backgroundColor: gradient?.from,
                    }}
                  />
                )}
              </Group>
            </Group>

            <Text size="xs" c="dimmed" lineClamp={2} mt={2} style={accessibilityStyles}>
              {notification.message}
            </Text>

            {notification.actions && notification.actions.length > 0 && (
              <Group gap="xs" mt="xs">
                {notification.actions.map((action, index) => (
                  <Button
                    key={index}
                    size="xs"
                    variant={action.variant === 'primary' ? 'filled' : action.variant === 'danger' ? 'light' : 'subtle'}
                    color={action.variant === 'danger' ? 'red' : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick();
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Group>
            )}
          </Box>

          <ActionIcon
            variant="subtle"
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(notification.id);
            }}
          >
            <IconX size={14} />
          </ActionIcon>
        </Group>
      </Box>
    );

    const notificationList = (items: NotificationItem[]) => (
      <ScrollArea h={maxHeight} type="auto">
        {items.length === 0 ? (
          <Box p="xl" ta="center">
            <IconBellOff size={40} color={theme.colors.gray[4]} />
            <Text c="dimmed" size="sm" mt="md" style={accessibilityStyles}>
              No notifications
            </Text>
          </Box>
        ) : (
          <Stack gap={0}>{items.map(renderNotification)}</Stack>
        )}
      </ScrollArea>
    );

    const trigger = triggerElement || (
      <Indicator
        disabled={unread === 0}
        label={unread > 99 ? '99+' : unread}
        size={16}
        color={gradient?.from}
      >
        <ActionIcon variant="subtle" size="lg">
          <IconBell size={20} />
        </ActionIcon>
      </Indicator>
    );

    return (
      <Popover
        opened={opened}
        onChange={toggle}
        position={position}
        width={380}
        shadow="lg"
        withArrow
      >
        <Popover.Target>
          <Box ref={ref} onClick={toggle} style={{ cursor: 'pointer' }}>
            {trigger}
          </Box>
        </Popover.Target>

        <Popover.Dropdown p={0}>
          {/* Header */}
          <Group justify="space-between" p="sm" style={{ borderBottom: `1px solid ${theme.colors.gray[2]}` }}>
            <Group gap="xs">
              <Text fw={600} style={accessibilityStyles}>Notifications</Text>
              {unread > 0 && (
                <Badge size="sm" variant="filled" color={gradient?.from}>
                  {unread} new
                </Badge>
              )}
            </Group>
            <Group gap="xs">
              {onMarkAllAsRead && unread > 0 && (
                <ActionIcon variant="subtle" size="sm" onClick={onMarkAllAsRead}>
                  <IconChecks size={16} />
                </ActionIcon>
              )}
              {onSettingsClick && (
                <ActionIcon variant="subtle" size="sm" onClick={onSettingsClick}>
                  <IconSettings size={16} />
                </ActionIcon>
              )}
            </Group>
          </Group>

          {/* Content */}
          {showTabs ? (
            <Tabs defaultValue="all">
              <Tabs.List>
                <Tabs.Tab value="all">All</Tabs.Tab>
                <Tabs.Tab value="unread">
                  Unread {unread > 0 && `(${unread})`}
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="all">{notificationList(allNotifications)}</Tabs.Panel>
              <Tabs.Panel value="unread">{notificationList(unreadNotifications)}</Tabs.Panel>
            </Tabs>
          ) : (
            notificationList(allNotifications)
          )}

          {/* Footer */}
          {notifications.length > 0 && onClearAll && (
            <Box p="sm" style={{ borderTop: `1px solid ${theme.colors.gray[2]}` }}>
              <Button
                variant="subtle"
                fullWidth
                size="xs"
                leftSection={<IconTrash size={14} />}
                onClick={onClearAll}
              >
                Clear all notifications
              </Button>
            </Box>
          )}
        </Popover.Dropdown>
      </Popover>
    );
  }
);

NotificationCenter.displayName = 'NotificationCenter';

export default NotificationCenter;
